'use client'
import { useState, useEffect } from 'react'
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth'
import { useRouter } from 'next/navigation'

export interface User {
  userId: string
  username: string
  signInDetails?: {
    loginId?: string
  }
}

export interface AuthSession {
  tokens?: {
    accessToken?: {
      payload?: {
        sub?: string
        'cognito:groups'?: string[]
      }
    }
    idToken?: {
      payload?: {
        sub?: string
        email?: string
        given_name?: string
        family_name?: string
        'custom:organization'?: string
        'cognito:groups'?: string[]
      }
    }
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const router = useRouter()

  useEffect(() => {
    checkAuthState()
  }, [])

  async function checkAuthState() {
    try {
      setIsLoading(true)
      setError(null)
      
      const currentUser = await getCurrentUser()
      const authSession = await fetchAuthSession()
      
      setUser(currentUser)
      setSession(authSession)
    } catch (err) {
      // User is not authenticated
      setUser(null)
      setSession(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSignOut() {
    try {
      await signOut()
      setUser(null)
      setSession(null)
      router.replace('/signin')
    } catch (err) {
      setError('Failed to sign out')
    }
  }

  const isAuthenticated = !!user && !!session
  const isAdmin = session?.tokens?.idToken?.payload?.['cognito:groups']?.includes('Admin') || false
  const isApprovedUser = session?.tokens?.idToken?.payload?.['cognito:groups']?.includes('Approved_Users') || false

  return {
    user,
    session,
    isLoading,
    error,
    isAuthenticated,
    isAdmin,
    isApprovedUser,
    checkAuthState,
    signOut: handleSignOut,
  }
}
